import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LEVEL_LABELS, RiskLevel } from '../types/risk';
import './AnalysisMonitorPage.css';

/**
 * Technical monitor for the legislative risk analysis pipeline.
 *
 * Lives at /admin/analyses. Polls /admin/risk-analysis/stats every 4s and
 * exposes the operational levers a developer needs:
 *  - Live status breakdown (done / analyzing / failed / pending) and percent
 *  - Provider distribution (anthropic-haiku / sonnet / google / heuristic)
 *  - In-memory counters from the current Node process (zeroes on restart)
 *  - List of recently-analyzed proposicoes
 *  - List of rows currently in flight (or zombie if not progressing)
 *  - Buttons to trigger bulk analysis, retry failed, and sweep stale rows
 *
 * Not gated behind auth in this iteration — it's an internal tool. Add a
 * simple shared-secret header check before exposing publicly.
 */

interface RecordedError {
  timestamp: string;
  service: string;
  event: string;
  message: string;
  context: Record<string, unknown>;
}

interface ProviderHealth {
  state: 'healthy' | 'rate_limited' | 'exhausted';
  availableAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  lastSuccessAt: string | null;
  successCount: number;
  failureCount: number;
}

interface Stats {
  status_breakdown: {
    done: number;
    analyzing: number;
    failed: number;
    pending: number;
    total: number;
  };
  level_distribution: {
    low: number;
    medium: number;
    high: number;
    average_score: number | null;
  };
  provider_distribution: Array<{ provider: string; count: number }>;
  analyzed_last_24h: number;
  in_memory_counters: Record<string, number>;
  recent_errors: RecordedError[];
  provider_health: Record<string, ProviderHealth>;
  provider_concurrency: Record<string, { inFlight: number; cap: number }>;
}

interface RecentItem {
  id: string;
  tipo: string;
  numero: number;
  ano: number;
  titulo: string;
  titulo_llm: string | null;
  ementa: string | null;
  ial_score: number | null;
  ial_level: RiskLevel | null;
  risk_analysis_status: string | null;
  risk_analysis_provider: string | null;
  risk_analysis_at: string;
}

interface InFlightItem {
  id: string;
  tipo: string;
  numero: number;
  ano: number;
  titulo: string;
  ementa: string | null;
  risk_analysis_status: string | null;
  risk_analysis_status_deep: string | null;
}

function displayTitle(it: {
  titulo?: string | null;
  titulo_llm?: string | null;
  ementa?: string | null;
}): string {
  const llm = (it.titulo_llm || '').trim();
  if (llm) return llm;
  const raw = (it.titulo || '').trim();
  if (raw) return raw;
  const ementa = (it.ementa || '').trim();
  if (!ementa) return '';
  return ementa.length > 140 ? ementa.slice(0, 137) + '…' : ementa;
}

const POLL_INTERVAL_MS = 4000;

function pct(n: number, total: number): string {
  if (total === 0) return '0%';
  return `${Math.round((n / total) * 100)}%`;
}

/**
 * Pretty-print an error message. Tries JSON, falls back to the raw string.
 * Also tries to find an embedded JSON object inside HTTP error wrappers like
 * "400 {...}" or "HTTP 429: {...}".
 */
function formatErrorBody(raw: string): string {
  const trimmed = raw.trim();
  // Direct JSON?
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    // ignore
  }
  // Embedded JSON ({...}) inside an HTTP wrapper string.
  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  if (start >= 0 && end > start) {
    const candidate = trimmed.slice(start, end + 1);
    try {
      const parsed = JSON.parse(candidate);
      const prefix = trimmed.slice(0, start).trim();
      return (prefix ? `${prefix}\n` : '') + JSON.stringify(parsed, null, 2);
    } catch {
      // ignore
    }
  }
  return raw;
}

/**
 * Pull a short, one-line summary out of a provider error for the collapsed
 * summary row. Tries `error.message` first, then the first line of the raw.
 */
function errorSummary(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    const msg =
      parsed?.error?.message ?? parsed?.message ?? parsed?.error?.type;
    if (typeof msg === 'string') return msg;
  } catch {
    // ignore
  }
  // Try embedded JSON.
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start >= 0 && end > start) {
    try {
      const parsed = JSON.parse(raw.slice(start, end + 1));
      const msg = parsed?.error?.message ?? parsed?.message;
      if (typeof msg === 'string') return msg;
    } catch {
      // ignore
    }
  }
  return raw.split('\n')[0];
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s atrás`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}min atrás`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h atrás`;
  return new Date(iso).toLocaleString('pt-BR');
}

export default function AnalysisMonitorPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const [inFlight, setInFlight] = useState<InFlightItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [bulkLimit, setBulkLimit] = useState(50);
  const [bulkConcurrency, setBulkConcurrency] = useState<number | null>(null);
  const [concurrencyTouched, setConcurrencyTouched] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, r, f] = await Promise.all([
        fetch('/api/admin/risk-analysis/stats').then((x) => x.json()),
        fetch('/api/admin/risk-analysis/recent?limit=30').then((x) => x.json()),
        fetch('/api/admin/risk-analysis/in-flight').then((x) => x.json()),
      ]);
      setStats(s as Stats);
      setRecent((r as { results: RecentItem[] }).results || []);
      setInFlight((f as { results: InFlightItem[] }).results || []);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  // Sync bulkConcurrency default with the sum of per-provider caps the
  // server reports. User input takes precedence once they've touched the
  // field.
  useEffect(() => {
    if (concurrencyTouched) return;
    if (!stats?.provider_concurrency) return;
    const totalCap = Object.values(stats.provider_concurrency).reduce(
      (acc, c) => acc + c.cap,
      0,
    );
    if (totalCap > 0) setBulkConcurrency(totalCap);
  }, [stats, concurrencyTouched]);

  const runAction = useCallback(
    async (label: string, url: string, method = 'POST') => {
      setBusy(label);
      try {
        const r = await fetch(url, { method });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        await refresh();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [refresh],
  );

  const sb = stats?.status_breakdown;
  const total = sb?.total ?? 0;

  return (
    <main className="monitor-page">
      <header className="monitor-header">
        <div>
          <span className="monitor-eyebrow">Operação</span>
          <h1>Monitor de análises</h1>
          <p className="monitor-sub">
            Pipeline do Índice de Atenção Legislativa (IAL). Atualização a cada{' '}
            {POLL_INTERVAL_MS / 1000}s.
          </p>
        </div>
        <div className="monitor-meta">
          {lastUpdate && (
            <span className="monitor-last-update">
              Atualizado às {lastUpdate.toLocaleTimeString('pt-BR')}
            </span>
          )}
          {error && <span className="monitor-error">⚠ {error}</span>}
        </div>
      </header>

      {/* Provider health */}
      {stats && (
        <section className="monitor-section">
          <span className="monitor-eyebrow-sm">Saúde dos provedores</span>
          <div className="monitor-providers">
            {Object.entries(stats.provider_health).map(([id, h]) => {
              const stateLabel =
                h.state === 'healthy'
                  ? 'OK'
                  : h.state === 'rate_limited'
                    ? 'Rate-limited'
                    : 'Esgotado';
              const availableAt = h.availableAt
                ? new Date(h.availableAt)
                : null;
              const secondsRemaining = availableAt
                ? Math.max(0, Math.round((availableAt.getTime() - Date.now()) / 1000))
                : 0;
              const conc = stats.provider_concurrency?.[id];
              const capPct = conc
                ? Math.min(100, (conc.inFlight / Math.max(1, conc.cap)) * 100)
                : 0;
              return (
                <article
                  key={id}
                  className={`monitor-provider monitor-provider-${h.state}`}
                >
                  <div className="monitor-provider-row">
                    <code className="monitor-provider-name">{id}</code>
                    <span className={`monitor-provider-state monitor-provider-state-${h.state}`}>
                      {stateLabel}
                    </span>
                    {conc && (
                      <div
                        className="monitor-provider-capacity"
                        title={`Em vôo: ${conc.inFlight} / cap: ${conc.cap}`}
                      >
                        <div className="monitor-provider-capacity-track">
                          <div
                            className="monitor-provider-capacity-fill"
                            style={{ width: `${capPct}%` }}
                          />
                        </div>
                        <span className="monitor-provider-capacity-label">
                          {conc.inFlight}/{conc.cap}
                        </span>
                      </div>
                    )}
                    <span className="monitor-provider-stats">
                      <strong>{h.successCount}</strong> ok ·{' '}
                      <strong>{h.failureCount}</strong> falha
                    </span>
                    {availableAt && h.state !== 'healthy' && (
                      <span className="monitor-provider-eta">
                        {secondsRemaining > 60
                          ? `disponível em ~${Math.round(secondsRemaining / 60)}min`
                          : `disponível em ${secondsRemaining}s`}
                      </span>
                    )}
                  </div>
                  {h.lastError && (
                    <details className="monitor-provider-error">
                      <summary>
                        <span className="monitor-provider-error-label">Última falha</span>
                        <span className="monitor-provider-error-summary">
                          {errorSummary(h.lastError)}
                        </span>
                      </summary>
                      <pre className="monitor-provider-error-body">
                        {formatErrorBody(h.lastError)}
                      </pre>
                    </details>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* Provider failure banner (legacy — kept for granular log of last 3) */}
      {stats && stats.recent_errors.length > 0 && (
        <section className="monitor-section monitor-alert">
          <div className="monitor-alert-head">
            <span className="monitor-alert-icon" aria-hidden>⚠</span>
            <div>
              <strong>Falhas recentes nos provedores</strong>
              <p>
                O <code>circuit breaker</code> põe o provedor em cooldown automaticamente.
                Enquanto todos estiverem indisponíveis, novas proposições ficam pendentes
                (não falhadas) e o worker para sozinho.
              </p>
            </div>
          </div>
          <ul className="monitor-alert-list">
            {stats.recent_errors.slice(0, 3).map((err, i) => {
              const provider = (err.context.provider as string) ?? '—';
              return (
                <li key={i}>
                  <span className="monitor-alert-time">
                    {new Date(err.timestamp).toLocaleTimeString('pt-BR')}
                  </span>
                  <code className="monitor-alert-provider">{provider}</code>
                  <span className="monitor-alert-message">{err.message}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Status breakdown */}
      <section className="monitor-section">
        <span className="monitor-eyebrow-sm">Estado no banco</span>
        <div className="monitor-cards">
          <article className="monitor-card monitor-card-done">
            <span className="monitor-card-label">
              <span className="monitor-dot monitor-dot-done" aria-hidden /> Concluídas
            </span>
            <span className="monitor-card-value">{sb?.done ?? '—'}</span>
            <span className="monitor-card-sub">{pct(sb?.done ?? 0, total)} do total</span>
          </article>
          <article className="monitor-card monitor-card-progress">
            <span className="monitor-card-label">
              <span className="monitor-dot monitor-dot-progress" aria-hidden /> Em análise
            </span>
            <span className="monitor-card-value">{sb?.analyzing ?? '—'}</span>
            <span className="monitor-card-sub">em vôo agora</span>
          </article>
          <article className="monitor-card monitor-card-fail">
            <span className="monitor-card-label">
              <span className="monitor-dot monitor-dot-fail" aria-hidden /> Falharam
            </span>
            <span className="monitor-card-value">{sb?.failed ?? '—'}</span>
            <span className="monitor-card-sub">{pct(sb?.failed ?? 0, total)} do total</span>
          </article>
          <article className="monitor-card monitor-card-pending">
            <span className="monitor-card-label">
              <span className="monitor-dot monitor-dot-pending" aria-hidden /> Pendentes
            </span>
            <span className="monitor-card-value">{sb?.pending ?? '—'}</span>
            <span className="monitor-card-sub">{pct(sb?.pending ?? 0, total)} do total</span>
          </article>
          <article className="monitor-card monitor-card-total">
            <span className="monitor-card-label">Total elegível</span>
            <span className="monitor-card-value">{total}</span>
            <span className="monitor-card-sub">com ementa ≥ 30 caracteres</span>
          </article>
        </div>
      </section>

      {/* Level distribution + 24h */}
      <section className="monitor-section monitor-grid-2">
        <div>
          <span className="monitor-eyebrow-sm">Distribuição por nível</span>
          <div className="monitor-distribution">
            {stats && (
              <>
                <div className="monitor-bar">
                  <span className="monitor-bar-label">Baixo</span>
                  <div className="monitor-bar-track">
                    <div
                      className="monitor-bar-fill monitor-bar-fill-low"
                      style={{
                        width: pct(
                          stats.level_distribution.low,
                          stats.level_distribution.low +
                            stats.level_distribution.medium +
                            stats.level_distribution.high || 1,
                        ),
                      }}
                    />
                  </div>
                  <span className="monitor-bar-count">
                    {stats.level_distribution.low}
                  </span>
                </div>
                <div className="monitor-bar">
                  <span className="monitor-bar-label">Médio</span>
                  <div className="monitor-bar-track">
                    <div
                      className="monitor-bar-fill monitor-bar-fill-medium"
                      style={{
                        width: pct(
                          stats.level_distribution.medium,
                          stats.level_distribution.low +
                            stats.level_distribution.medium +
                            stats.level_distribution.high || 1,
                        ),
                      }}
                    />
                  </div>
                  <span className="monitor-bar-count">
                    {stats.level_distribution.medium}
                  </span>
                </div>
                <div className="monitor-bar">
                  <span className="monitor-bar-label">Alto</span>
                  <div className="monitor-bar-track">
                    <div
                      className="monitor-bar-fill monitor-bar-fill-high"
                      style={{
                        width: pct(
                          stats.level_distribution.high,
                          stats.level_distribution.low +
                            stats.level_distribution.medium +
                            stats.level_distribution.high || 1,
                        ),
                      }}
                    />
                  </div>
                  <span className="monitor-bar-count">
                    {stats.level_distribution.high}
                  </span>
                </div>
                <p className="monitor-distribution-foot">
                  IAL médio: <strong>{stats.level_distribution.average_score ?? '—'}</strong>
                </p>
              </>
            )}
          </div>
        </div>

        <div>
          <span className="monitor-eyebrow-sm">Atividade</span>
          <dl className="monitor-dl">
            <div>
              <dt>Analisadas nas últimas 24 horas</dt>
              <dd>{stats?.analyzed_last_24h ?? '—'}</dd>
            </div>
            <div>
              <dt>Em vôo agora</dt>
              <dd>{inFlight.length}</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Provider distribution */}
      <section className="monitor-section">
        <span className="monitor-eyebrow-sm">Distribuição por provedor</span>
        <table className="monitor-table">
          <thead>
            <tr>
              <th>Provedor</th>
              <th>Análises</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {stats?.provider_distribution.map((p) => (
              <tr key={p.provider}>
                <td>
                  <code>{p.provider}</code>
                </td>
                <td>{p.count}</td>
                <td>{pct(p.count, sb?.done ?? 1)}</td>
              </tr>
            )) ?? null}
            {stats && stats.provider_distribution.length === 0 && (
              <tr>
                <td colSpan={3} className="monitor-table-empty">
                  Nenhuma análise concluída ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* In-flight */}
      <section className="monitor-section">
        <span className="monitor-eyebrow-sm">
          Em vôo agora{' '}
          <span className="monitor-inline-meta">
            ({inFlight.length})
          </span>
        </span>
        {inFlight.length === 0 ? (
          <p className="monitor-empty">Nenhuma análise em vôo.</p>
        ) : (
          <ul className="monitor-list">
            {inFlight.map((it) => (
              <li key={it.id} className="monitor-list-item">
                <Link to={`/detail/${it.id}`} className="monitor-list-ref">
                  {it.tipo} {it.numero}/{it.ano}
                </Link>
                <span className="monitor-list-title">{displayTitle(it)}</span>
                <span className="monitor-list-state">
                  {it.risk_analysis_status === 'analyzing' && 'rasa'}
                  {it.risk_analysis_status_deep === 'analyzing' && ' profunda'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Recent */}
      <section className="monitor-section">
        <span className="monitor-eyebrow-sm">
          Últimas análises concluídas
          <span className="monitor-inline-meta">
            ({recent.length} mostradas)
          </span>
        </span>
        {recent.length === 0 ? (
          <p className="monitor-empty">Nenhuma análise concluída ainda.</p>
        ) : (
          <div className="monitor-list-scroll">
            <ul className="monitor-list monitor-list-compact">
              {recent.map((it) => (
                <li key={it.id} className="monitor-list-item">
                  <span
                    className={`monitor-pill monitor-pill-${it.ial_level ?? 'low'}`}
                    title="Índice de Atenção Legislativa"
                  >
                    {it.ial_score ?? '—'}
                  </span>
                  <Link to={`/detail/${it.id}`} className="monitor-list-ref">
                    {it.tipo} {it.numero}/{it.ano}
                  </Link>
                  <span className="monitor-list-title">
                    {displayTitle(it)}
                  </span>
                  <code className="monitor-list-provider-code">
                    {it.risk_analysis_provider ?? '—'}
                  </code>
                  <span className="monitor-list-time">
                    {timeAgo(it.risk_analysis_at)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* In-memory counters */}
      <section className="monitor-section">
        <span className="monitor-eyebrow-sm">
          Contadores do processo{' '}
          <span className="monitor-inline-meta">
            (zeram a cada reinício do servidor)
          </span>
        </span>
        {stats && Object.keys(stats.in_memory_counters).length === 0 ? (
          <p className="monitor-empty">Sem atividade desde o último restart.</p>
        ) : (
          <table className="monitor-table">
            <tbody>
              {stats &&
                Object.entries(stats.in_memory_counters).map(([k, v]) => (
                  <tr key={k}>
                    <td>
                      <code>{k}</code>
                    </td>
                    <td>{v}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Operações */}
      <section className="monitor-section monitor-actions-section">
        <span className="monitor-eyebrow-sm">Operações</span>
        <div className="monitor-actions-grid">
          <div className="monitor-action-card">
            <h3>Processar pendentes</h3>
            <p>
              Enfileira até <code>limite</code> proposições pendentes (ou falhas)
              e processa com <code>concorrência</code> workers em paralelo.
              Default = soma dos caps por provedor (
              {stats
                ? Object.values(stats.provider_concurrency).reduce(
                    (acc, c) => acc + c.cap,
                    0,
                  )
                : '—'}
              ).
            </p>
            <div className="monitor-action-row">
              <label>
                Limite
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={bulkLimit}
                  onChange={(e) => setBulkLimit(Number(e.target.value))}
                />
              </label>
              <label>
                Concorrência
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={bulkConcurrency ?? ''}
                  placeholder="auto"
                  onChange={(e) => {
                    setConcurrencyTouched(true);
                    setBulkConcurrency(
                      e.target.value === '' ? null : Number(e.target.value),
                    );
                  }}
                />
              </label>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => {
                  const c = bulkConcurrency ?? '';
                  const qs = c
                    ? `?limit=${bulkLimit}&concurrency=${c}`
                    : `?limit=${bulkLimit}`;
                  runAction(
                    'analyze-all-pending',
                    `/api/proposicoes/risk-analysis/analyze-all-pending${qs}`,
                  );
                }}
              >
                {busy === 'analyze-all-pending' ? 'Enfileirando…' : 'Disparar'}
              </button>
            </div>
          </div>

          <div className="monitor-action-card">
            <h3>Retomar falhas</h3>
            <p>Reseta todas as proposições com falha e as reenfileira para nova tentativa.</p>
            <button
              type="button"
              disabled={!!busy}
              onClick={() =>
                runAction(
                  'retry-all-failed',
                  '/api/proposicoes/risk-analysis/retry-all-failed',
                )
              }
            >
              {busy === 'retry-all-failed' ? 'Reenfileirando…' : 'Retomar todas'}
            </button>
          </div>

          <div className="monitor-action-card">
            <h3>Backfill heurístico</h3>
            <p>
              Aplica a análise heurística (regex/keywords, ~5ms/row, grátis)
              em todas as proposições sem análise. Idempotente — não toca
              em rows já processadas por LLM.
            </p>
            <button
              type="button"
              disabled={!!busy}
              onClick={() =>
                runAction(
                  'backfill-heuristic',
                  '/api/proposicoes/risk-analysis/backfill-heuristic',
                )
              }
            >
              {busy === 'backfill-heuristic' ? 'Processando…' : 'Rodar backfill'}
            </button>
          </div>

          <div className="monitor-action-card">
            <h3>Limpar zumbis</h3>
            <p>
              Reseta linhas presas em <code>em análise</code> (após queda ou
              reinício do servidor). O startup já faz isso automaticamente.
            </p>
            <button
              type="button"
              disabled={!!busy}
              onClick={() =>
                runAction('sweep-stale', '/api/admin/risk-analysis/sweep-stale')
              }
            >
              {busy === 'sweep-stale' ? 'Limpando…' : 'Limpar agora'}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
